import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class LocalMoransI extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.localmoransi',
        group: 'Statistics',
        title: 'Local Morans I',
        description: `
                    This function computes the local Morans I statistic for each index in the input table.
                `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'indexcol',
          type: WorkflowComponentParamType.Column,
          title: 'Index column',
          parent: 'source',
          dataType: [ColumnDataType.String, ColumnDataType.Number]
        },
        {
          name: 'valuecol',
          type: WorkflowComponentParamType.Column,
          title: 'Value column',
          parent: 'source',
          dataType: [ColumnDataType.Number]
        },
        {
          name: 'size',
          type: WorkflowComponentParamType.Number,
          title: 'Size',
          default: 3,
          min: 1,
          max: 10
        },
        {
          name: 'decay',
          type: WorkflowComponentParamType.Selection,
          title: 'Decay function',
          options: ['uniform', 'inverse', 'inverse_square', 'exponential']
        },
        {
          name: 'permutations',
          type: WorkflowComponentParamType.Number,
          title: 'Permutations',
          min: 1,
          max: 1000,
          default: 1
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const preparedParams = this._prepareParameters(params)
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)
    const source = preparedParams.source as string
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )

    let query = ''
    switch (connection.type) {
      case Provider.BigQuery: {
        let outTable = tablename
        let copyFromTempTable = ''
        if (codeType === WorkflowCodeType.StoredProcedure) {
          outTable = `${connection.tempLocation}.${tempTableName(
            componentName,
            preparedParams.source
          )}`
          copyFromTempTable = connection.getCreateTempTableQuery(
            tablename,
            `SELECT * FROM ${connection.quoteName(outTable)}`,
            codeType,
            null
          )
        }
        query = dedent`
            DECLARE grid_type STRING;
            DECLARE grid_resolution INT64;

            CALL ${at}.__CHECK_GRID_INDEX_COLUMN(
              'SELECT * FROM ${connection.quoteName(source)}',
              '${params.indexcol as string}',
              grid_type,
              grid_resolution
            );

            IF grid_type = 'quadbin' THEN
              CALL ${at}.LOCAL_MORANS_I_QUADBIN_TABLE(
                '${source}',
                '${connection.quoteName(outTable)}',
                '${params.indexcol as string}',
                '${params.valuecol as string}',
                ${params.size},
                '${params.decay}',
                ${params.permutations}
              );
            ELSEIF grid_type = 'h3' THEN
              CALL ${at}.LOCAL_MORANS_I_H3_TABLE(
                '${source}',
                '${connection.quoteName(outTable)}',
                '${params.indexcol as string}',
                '${params.valuecol as string}',
                ${params.size},
                '${params.decay}',
                ${params.permutations}
              );
            ELSE
              RAISE USING MESSAGE = 'Invalid spatial index column, please select a column that contains h3 or quadbin indexes.';
            END IF;
            ${copyFromTempTable}
            `
        break
      }
      case Provider.Snowflake:
        query = dedent`
              DECLARE
                grid_type STRING;
                WRONG_GRID_TYPE_EXCEPTION EXCEPTION;
              BEGIN
                grid_type := (
                  CALL ${at}._CHECK_GRID_INDEX_COLUMN(
                    'SELECT * FROM ${source}',
                    '${params.indexcol as string}'
                  )
                );
                IF (grid_type != 'unsupported') THEN
                  EXECUTE IMMEDIATE '
                      CALL ${at}.LOCAL_MORANS_I_' || grid_type || '(
                          ''${source}'',
                          ''${tablename}'',
                          ''${params.indexcol as string}'',
                          ''${params.valuecol as string}'',
                          ${params.size},
                          ''${params.decay}'',
                          ${params.permutations}
                      );
                  ';
                ELSE
                  RAISE WRONG_GRID_TYPE_EXCEPTION;
                END IF;
              END;
            `
        break
    }

    query = connection.wrapCodeInIfTableExistsBlock(
      query,
      [tablename],
      codeType
    )

    return {
      outputs: { result: tablename },
      query
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({
    columns,
    params,
    connection
  }: GetOutputSchemaParams): TableSchema {
    const indexType = columns.dryrun.find(
      (c) => c.name === params.indexcol
    )?.type
    let indexName = 'index'
    if (connection.type === Provider.Snowflake) {
      indexName = indexType === ColumnDataType.String ? 'h3' : 'quadbin'
    }
    return [
      {
        name: indexName,
        type: indexType as ColumnDataType
      },
      {
        name: 'value',
        type: ColumnDataType.Number
      },
      {
        name: 'psim',
        type: ColumnDataType.Number
      },
      {
        name: 'EIc',
        type: ColumnDataType.Number
      },
      {
        name: 'VIc',
        type: ColumnDataType.Number
      },
      {
        name: 'EI',
        type: ColumnDataType.Number
      },
      {
        name: 'VI',
        type: ColumnDataType.Number
      },
      {
        name: 'quad',
        type: ColumnDataType.Number
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake]
  }
}

export { LocalMoransI }
